// PublicStrategies.js
// Lightweight public view that keeps the familiar sidebar layout while showing
// the Investment Strategies content without requiring authentication.
import React, { useState } from 'react';
import { X, Menu, LogIn, Moon, Sun } from 'lucide-react';
import StrategiesSection from './StrategiesSection';
import { useTheme } from '../theme/ThemeContext';

const PublicStrategies = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  return (
    <div
      className="flex h-screen bg-[#0A0A0A] text-white overflow-hidden"
      style={{ fontFamily: 'Lexend, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
    >
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-72 max-w-[85vw] sm:w-72 lg:w-80
          bg-[#151515]/95 backdrop-blur
          border-r border-[#676DFF]/20
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Mobile Close Button */}
        <button
          onClick={() => setIsSidebarOpen(false)}
          className="absolute top-4 right-4 lg:hidden p-2 text-[#B8B8B8] hover:text-white hover:bg-[#676DFF]/10 rounded-lg transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Logo Section */}
        <div className="p-6 lg:p-8 border-b border-[#676DFF]/10">
          <div className="flex items-center mb-4 lg:mb-6">
            <img
              src="/images/logoOnly.png"
              alt="AB Nexus Logo"
              className="w-7 h-7 lg:w-12 lg:h-12 mr-3"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="text-lg lg:text-xl font-semibold text-white tracking-wider">
              AB NEXUS
            </div>
          </div>
          <div className="text-left">
            <div className="text-sm text-[#B8B8B8] font-normal">Explore our strategies</div>
            <div className="mt-2 text-xs text-[#808080]">
              View yields, NAV, and details without creating an account.
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <div className="space-y-6">
            <div>
              <div className="text-xs text-[#808080] uppercase tracking-wider font-semibold mb-3 lg:mb-4 px-2">
                Portfolio
              </div>
              <button
                className="w-full flex items-center px-4 lg:px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 bg-[#676DFF]/15 text-[#676DFF] border-r-3 border-[#676DFF]"
              >
                <span className="mr-3">
                  <svg className="h-4 w-4 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <rect x="9" y="9" width="6" height="6"></rect>
                  </svg>
                </span>
                <span className="flex-1 text-left">Investment Strategies</span>
              </button>
            </div>
          </div>
        </nav>

        {/* CTA */}
        <div className="p-4 lg:p-6 border-t border-[#676DFF]/10">
          <button
            onClick={() => (window.location.href = '/login')}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-[#676DFF] hover:bg-[#5A60E8] text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <LogIn className="h-4 w-4" />
            <span>Login / Signup</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="flex items-center justify-between px-4 lg:px-8 py-4 border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur z-10">
          <button
            className="lg:hidden p-2 rounded-lg text-[#B8B8B8] hover:text-white hover:bg-white/5 transition-colors"
            onClick={() => setIsSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1 text-center lg:text-left">
            <div className="text-xs uppercase tracking-wider text-[#808080]">Public view</div>
            <div className="text-lg font-semibold text-white">Investment Strategies</div>
          </div>
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-medium flex items-center gap-2 transition-colors"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={() => (window.location.href = '/login')}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors"
            >
              Login / Signup
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto py-6 lg:py-8 px-4 lg:px-8">
            <StrategiesSection
              userStatus={null}
              kycStatus={null}
              client={null}
              currentView="list"
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default PublicStrategies;

